/**
 * Tools dashboard showing available features as cards.
 * Only shows tools the user has access to.
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { toolsRoute } from './index';
import { useAuth, hasFeature } from '@/contexts/AuthContext';
import { Plane, Grid3X3, Armchair, BarChart3, Terminal, DollarSign } from 'lucide-react';

interface ToolCard {
  id: string;
  feature: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  gradient: string;
}

const TOOL_CARDS: ToolCard[] = [
  {
    id: 'bookings',
    feature: 'bookings',
    title: 'Buchungen',
    description: 'Lufthansa-Buchungen suchen und Änderungen überwachen',
    icon: Plane,
    path: '/tools/bookings',
    gradient: 'from-blue-500 to-blue-600',
  },
  {
    id: 'availability',
    feature: 'availability',
    title: 'Verfügbarkeit',
    description: 'Flugverfügbarkeit und Buchungsklassen abfragen',
    icon: Grid3X3,
    path: '/tools/availability',
    gradient: 'from-emerald-500 to-emerald-600',
  },
  {
    id: 'seatmap',
    feature: 'seatmap',
    title: 'Seat Map',
    description: 'Sitzplatzkonfiguration und Verfügbarkeit anzeigen',
    icon: Armchair,
    path: '/tools/seatmap',
    gradient: 'from-violet-500 to-violet-600',
  },
  {
    id: 'flight_status',
    feature: 'flight_status',
    title: 'Flugstatus',
    description: 'Flugstatus und Verspätungen in Echtzeit prüfen',
    icon: BarChart3,
    path: '/tools/flight-status',
    gradient: 'from-amber-500 to-amber-600',
  },
  {
    id: 'console',
    feature: 'amadeus_console',
    title: 'Amadeus Console',
    description: 'Direkte Amadeus Cryptic Befehle ausführen',
    icon: Terminal,
    path: '/tools/console',
    gradient: 'from-slate-700 to-slate-800',
  },
  {
    id: 'pricing',
    feature: 'retro_pricing',
    title: 'Fair Pricing',
    description: 'Historische Preisanalyse für Flugbuchungen',
    icon: DollarSign,
    path: '/tools/pricing',
    gradient: 'from-teal-500 to-teal-600',
  },
];

function ToolsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Filter tools based on user's feature access
  const availableTools = TOOL_CARDS.filter(tool =>
    hasFeature(user, tool.feature)
  );

  if (availableTools.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Keine Werkzeuge verfügbar.</p>
        <p className="text-sm text-slate-400 mt-2">
          Bitte wenden Sie sich an den Administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {availableTools.map(tool => {
        const Icon = tool.icon;
        return (
          <button
            key={tool.id}
            onClick={() => navigate({ to: tool.path })}
            className="group relative bg-white rounded-xl border border-slate-200 p-6 text-left hover:shadow-lg hover:border-slate-300 transition-all duration-200"
          >
            <div
              className={`h-12 w-12 rounded-lg bg-gradient-to-br ${tool.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}
            >
              <Icon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {tool.title}
            </h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              {tool.description}
            </p>
            <div className="absolute inset-0 rounded-xl ring-2 ring-transparent group-hover:ring-blue-500/20 transition-all duration-200" />
          </button>
        );
      })}
    </div>
  );
}

export const toolsDashboardRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: '/',
  component: ToolsDashboard,
});
